import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { FineDetailsPublicResponse } from "api"
import { saveAs } from "file-saver"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useEffect } from "react"
import { busPaymentReportSuccess, Link } from "utils"

const SuccessBox = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  text-align: center;
`

const SuccessTextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DetailsBox = styled.div`
  display: flex;
  padding: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 24px 24px 32px 24px;
  }
  flex-direction: column;
  gap: 32px;
  border-radius: 12px;
  border: 1px solid #d1d5db;

  box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
`

const DetailsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 32px;
  }
`

const DetailsGrid = styled.div<{ isPayerDetails?: boolean }>`
  display: grid;
  grid-template-columns: ${({ isPayerDetails }) =>
    isPayerDetails ? "1fr 1fr" : "1fr"};
  padding-bottom: 32px;
  gap: 32px;
  border-bottom: ${({ isPayerDetails }) =>
    isPayerDetails ? "none" : "1px solid var(--Grey-Gray-M, #d1d5db)"};
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 24px;
  }
`

const ButtonFlex = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up("md")} {
    align-self: center;
    gap: 24px;
    flex-direction: row;
    width: 100%;
    max-width: 528px;
  }
`

const DetailGridItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default function Success({
  data,
  iframeData,
}: {
  data: FineDetailsPublicResponse
  iframeData: {
    confirmationNumber: string
    email: string
    firstname: string
    lastname: string
    cardholderid: string
    pdf: string
    phone: string
  }
}) {
  const t = useTranslations("Forms")
  const pdf = {
    file: `data:application/pdf;base64,${iframeData.pdf}`,
    file_name: "Purchase Invoice",
  }
  const handleDownload = () => {
    try {
      // Convert base64 to binary
      const base64Data = iframeData.pdf
      const binaryString = atob(base64Data)
      const bytes = new Uint8Array(binaryString.length)

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }

      // Create blob and download using file-saver
      const blob = new Blob([bytes], { type: "application/pdf" })
      const fileName = `${pdf.file_name}.pdf`
      saveAs(blob, fileName)
    } catch (error) {
      console.error("Download failed:", error)
      // Fallback: open in new window (Safari iOS behavior)
      window.open(pdf.file, "_blank")
    }
  }
  const decodedFirstname = document.createElement("textarea")
  decodedFirstname.innerHTML = iframeData.pdf
  useEffect(() => {
    busPaymentReportSuccess()
  }, [])
  return (
    <>
      <SuccessBox role="alert">
        <Image
          src={"/icons/formSuccess.gif"}
          width={200}
          height={200}
          alt="success"
        />
        <SuccessTextBox>
          <Typography variant="h2">{t("paymentReceived")}</Typography>
          <Typography variant="body1">{t("sentPayment")}</Typography>
        </SuccessTextBox>
      </SuccessBox>
      <DetailsBox>
        <DetailsContent>
          <Typography variant="h4" component={"h3"}>
            {t("paymentDetails")}
          </Typography>
          <DetailsGrid>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("referenceNum")}
              </Typography>
              <Typography variant="body1">
                {iframeData.confirmationNumber}
              </Typography>
            </DetailGridItem>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("amountPaid")}
              </Typography>
              <Typography variant="body1">₪{data.fineAmount}</Typography>
            </DetailGridItem>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("reportNumber")}
              </Typography>
              <Typography variant="body1">{data.fineId}</Typography>
            </DetailGridItem>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("formNumber")}
              </Typography>
              <Typography variant="body1">{data.fineNumerator}</Typography>
            </DetailGridItem>
          </DetailsGrid>
        </DetailsContent>
        <DetailsContent>
          <Typography variant="h2" component={"h3"}>
            {t("payerDetails")}
          </Typography>
          <DetailsGrid isPayerDetails>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("firstName")}
              </Typography>
              <Typography variant="body1">{iframeData.firstname}</Typography>
            </DetailGridItem>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("lastName")}
              </Typography>
              <Typography variant="body1">{iframeData.lastname}</Typography>
            </DetailGridItem>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("idCard")}
              </Typography>
              <Typography variant="body1">{iframeData.cardholderid}</Typography>
            </DetailGridItem>
            <DetailGridItem>
              <Typography variant="body1" fontWeight={700}>
                {t("phone1")}
              </Typography>
              <Typography variant="body1">{iframeData.phone}</Typography>
            </DetailGridItem>
          </DetailsGrid>
        </DetailsContent>
      </DetailsBox>
      <ButtonFlex>
        <Button variant="primary" fullWidth onClick={handleDownload}>
          {t("downloadPayment")}
        </Button>
        <Button variant="secondary" fullWidth LinkComponent={Link} href="/">
          {t("backToHomepage")}
        </Button>
      </ButtonFlex>
    </>
  )
}
